import React from 'react';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { take } from 'lodash';
import dayjs from 'dayjs';

import { ReactComponent as HeroImage } from '../graphics/svg/home.svg';
import {
  useScenarioListQuery,
  useSimulationTypesQuery,
} from '../network/queries';
import { ScenarioTable } from '../components/scenarios';
import { Loader } from '../components/common';

/**
 * Home page.
 * @returns {React.FunctionComponent} React element that contains the page.
 */
function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isLoading: isLoadingSimulationTypes, data: simulationTypes } =
    useSimulationTypesQuery();
  const { isLoading: isLoadingScenarios, data: scenarios } =
    useScenarioListQuery();

  // Exclude base scenarios
  const recentScenarios = take(
    isLoadingSimulationTypes || isLoadingScenarios
      ? []
      : scenarios
          .filter(
            (s) => !simulationTypes.find((t) => s.id === t.default_scenario_id),
          )
          .sort((a, b) =>
            dayjs(b.updated_timestamp).diff(dayjs(a.updated_timestamp)),
          ),
    5,
  );

  return isLoadingScenarios || isLoadingSimulationTypes ? (
    <Loader />
  ) : (
    <Grid
      container
      spacing={3}
      sx={{
        flex: 1,
        height: '100%',
        width: '100%',
      }}
    >
      <Grid size={5}>
        <HeroImage />
      </Grid>
      <Grid size={7}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                {t('pages.home.title')}
              </Typography>
              <Typography>{t('pages.home.titleContent1')}</Typography>
              <Typography>{t('pages.home.titleContent2')}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={3}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                {t('pages.home.scenarios')}
              </Typography>
              <Typography>{t('pages.home.scenariosContent')}</Typography>
              <Button
                variant="contained"
                onClick={() => navigate(`../scenarios`)}
                sx={{ alignSelf: 'center' }}
              >
                {t('pages.home.scenariosButton')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={9}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                {t('pages.home.recentScenarios')}
              </Typography>
              <ScenarioTable scenarios={recentScenarios} />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default HomePage;
